
import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

function useStepReveal(index: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

export default function HowItWorks() {
  const { t } = useTranslation();

  // Safe wrapper around t() to avoid runtime crashes when keys are missing
  const safeT = (key: string) => {
    try {
      const result = t(key);
      return result ?? key;
    } catch {
      return key;
    }
  };

  const steps = [
    {
      number: '01',
      icon: 'ri-plant-line',
      title: safeT('process.s1.title'),
      description: safeT('process.s1.desc'),
    },
    {
      number: '02',
      icon: 'ri-settings-3-line',
      title: safeT('process.s2.title'),
      description: safeT('process.s2.desc'),
    },
    {
      number: '03',
      icon: 'ri-ship-line',
      title: safeT('process.s3.title'),
      description: safeT('process.s3.desc'),
    },
  ];

  return (
    <section id="proceso" className="py-12 sm:py-24 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://public.readdy.ai/ai/img_res/000f4cf3b173a8e7807c0324642ee0c6.jpg)`,
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/85 to-white/90" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 relative z-10">
        <div className="text-center mb-10 sm:mb-20">
          <p className="text-xs sm:text-sm font-semibold text-[#6B8E23] uppercase tracking-wide mb-2 sm:mb-3">
            {safeT('process.tag')}
          </p>
          <h2 className="text-[1.4rem] leading-tight sm:text-4xl md:text-5xl font-bold text-[#1A1A1A]">
            {safeT('process.title')}
          </h2>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-[#E8F5E9]">
            <div className="absolute inset-0 flex justify-between px-32">
              <div className="w-8 h-0.5 bg-[#6B8E23]"></div>
              <div className="w-8 h-0.5 bg-[#6B8E23]"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-12">
            {steps.map((step, index) => (
              <StepCard key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({
  step,
  index,
}: {
  step: { number: string; icon: string; title: string; description: string };
  index: number;
}) {
  const { ref, visible } = useStepReveal(index);
  const [bounced, setBounced] = useState(false);

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setBounced(true), index * 150 + 300);
      return () => clearTimeout(timer);
    }
  }, [visible, index]);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 150}ms` }}
      className={`relative text-center transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      {/* Número con animación de salto */}
      <div className="flex justify-center mb-3 sm:mb-6">
        <div
          style={{
            transitionDelay: `${index * 150 + 200}ms`,
            animation: bounced
              ? `stepBounce 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97) ${index *
                  150}ms both`
              : 'none',
          }}
          className={`w-14 h-14 sm:w-20 sm:h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center
            transition-transform duration-500
            ${bounced ? 'scale-110' : 'scale-100'}`}
        >
          <span className="text-lg sm:text-2xl font-bold text-[#03912c]">
            {step.number}
          </span>
        </div>
      </div>

      <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2 sm:mb-4">
        <i className={`${step.icon} text-2xl sm:text-4xl text-[#6B8E23]`} aria-hidden="true"></i>
      </div>

      <h3 className="text-lg sm:text-2xl font-bold text-[#1A1A1A] mb-2 sm:mb-4">
        {step.title}
      </h3>

      <p className="text-xs sm:text-base text-[#666666] leading-relaxed">
        {step.description}
      </p>
    </div>
  );
}
